import Papa from 'papaparse';

export interface CsvParseResult {
  columns: string[];
  data: (string | null)[][];
  errors?: Papa.ParseError[];
}

export const parseCSVHeaders = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      preview: 1, // Only parse the first row to get headers
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
          return;
        }
        
        if (results.data.length === 0) {
          reject(new Error('CSV file is empty'));
          return;
        }
        
        const headers = results.data[0] as string[];
        if (!headers || headers.length === 0) {
          reject(new Error('No headers found in CSV file'));
          return;
        }
        
        // Clean up headers (trim whitespace, handle empty names)
        const cleanHeaders = headers.map((header, index) => {
          const cleaned = String(header || '').trim();
          return cleaned || `Column_${index + 1}`;
        });
        
        resolve(cleanHeaders);
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    });
  });
};

export const parseCSVData = (
  file: File, 
  selectedColumns: string[], 
  allColumns: string[]
): Promise<CsvParseResult> => {
  return new Promise((resolve, reject) => {
    const selectedIndexes = selectedColumns.map(col => allColumns.indexOf(col));
    
    if (selectedIndexes.some(index => index === -1)) {
      reject(new Error('Some selected columns were not found in the CSV'));
      return;
    }
    
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      worker: true, // Use web worker for better performance
      step: undefined, // We'll parse everything at once for now
      complete: (results) => {
        if (results.errors.length > 0) {
          const criticalErrors = results.errors.filter(error => error.type === 'Delimiter');
          if (criticalErrors.length > 0) {
            reject(new Error(`CSV parsing error: ${criticalErrors[0].message}`));
            return;
          }
        }
        
        if (results.data.length < 2) {
          reject(new Error('CSV file must have at least one data row'));
          return;
        }
        
        // Skip the header row and extract only selected columns
        const dataRows = results.data.slice(1) as string[][];
        const filteredData = dataRows.map(row => 
          selectedIndexes.map(index => row[index] || null)
        );
        
        resolve({
          columns: selectedColumns,
          data: filteredData,
          errors: results.errors
        });
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    });
  });
};

export const validateCSVFile = (file: File): string | null => {
  // Check file type
  if (!file.type.includes('csv') && !file.name.toLowerCase().endsWith('.csv')) {
    return 'Please select a valid CSV file';
  }
  
  // Check file size (limit to 1GB)
  const maxSize = 1024 * 1024 * 1024; // 1GB
  if (file.size > maxSize) {
    return 'File size must be less than 1GB';
  }
  
  // Check if file is empty
  if (file.size === 0) {
    return 'File cannot be empty';
  }
  
  return null; // No errors
};

export const convertToParquetCompatibleData = (data: string[][]): any[][] => {
  return data.map(row => 
    row.map(cell => {
      if (cell === null || cell === undefined || cell === '') {
        return null;
      }
      
      // Try to convert to number if possible
      const numValue = Number(cell);
      if (!isNaN(numValue) && isFinite(numValue)) {
        return numValue;
      }
      
      // Check for boolean values
      const lowerCell = cell.toLowerCase();
      if (lowerCell === 'true' || lowerCell === 'false') {
        return lowerCell === 'true';
      }
      
      // Return as string
      return String(cell);
    })
  );
}; 